import * as React from 'react'
import { v4 as uuid } from 'uuid';

import { ESCAPE, } from '../../../Shared'
import { onMobile } from '../../libs/onMobile';

interface TooltipProps {
  averageItemHeight?: number;
  closeOnTriggerClick?: boolean;
  constrainToPane?: boolean;
  fixedPosition?: boolean;
  handleClick?: (e: any) => void;
  hugContent?: boolean;
  isTabbable?: boolean;
  mode?: string;
  skipPositioning?: boolean;
  tooltipMaxWidth?: number;
  tooltipText: string | Array<string>;
  tooltipTriggerId?: string;
  tooltipTriggerStyle?: { [key: string]: any };
  tooltipTriggerText: string | JSX.Element | JSX.Element[] | string[];
  tooltipTriggerTextClass?: string;
  tooltipTriggerTextStyle?: { [key: string]: any };
}

const DEFAULT_WIDTH = 300;
const MIN_WIDTH = 150;
const VIEWPORT_PADDING = 8;
const FOCUSABLE_TRIGGER_TAGS = ['a', 'button', 'input', 'select', 'summary', 'textarea'];
const CLIPPING_OVERFLOW_VALUES = ['hidden', 'auto', 'scroll', 'clip', 'overlay'];

// The popup is absolutely positioned inside the trigger, so any ancestor with a
// non-visible overflow clips it long before it reaches a viewport edge -- that is what
// sliced Evidence's tooltips in half at the edge of the two scrolling panes, which sit
// well inside the viewport. Only consulted for callers that pass `constrainToPane`.
// Either axis counts: CSS resolves `visible` on one axis to `auto` when the other is not
// visible, so an overflow-y scroller clips horizontally too.
const nearestClippingAncestorRect = (element: HTMLElement): DOMRect | null => {
  let current = element.parentElement;

  while (current && current !== document.body && current !== document.documentElement) {
    const { overflowX, overflowY } = window.getComputedStyle(current);

    if (CLIPPING_OVERFLOW_VALUES.includes(overflowX) || CLIPPING_OVERFLOW_VALUES.includes(overflowY)) {
      return current.getBoundingClientRect();
    }

    current = current.parentElement;
  }

  return null;
};

// Centers the tooltip on its trigger, then slides it back inside whichever edge it would
// otherwise spill past. The viewport is always a bound; the clipping ancestor is only one
// when the caller opts in via `constrainToPane`, so tooltips everywhere else keep the
// viewport-only placement they have always had. Narrows the tooltip first when the pane is
// too tight to hold it at full width, down to MIN_WIDTH -- below that it stays readable and
// accepts the overflow. The returned `left` is relative to the trigger, since the wrapper is
// absolutely positioned within it; `viewportLeft` is the same placement in viewport
// coordinates for `fixedPosition` callers, whose wrapper is fixed instead.
const horizontalPlacement = (trigger: HTMLElement, targetWidth: number, constrainToPane: boolean, minWidth = MIN_WIDTH) => {
  const triggerRect = trigger.getBoundingClientRect();
  const clipRect = constrainToPane ? nearestClippingAncestorRect(trigger) : null;

  let leftBound = VIEWPORT_PADDING;
  let rightBound = window.innerWidth - VIEWPORT_PADDING;

  if (clipRect) {
    leftBound = Math.max(leftBound, clipRect.left + VIEWPORT_PADDING);
    rightBound = Math.min(rightBound, clipRect.right - VIEWPORT_PADDING);
  }

  const width = Math.max(Math.min(targetWidth, rightBound - leftBound), minWidth);
  let left = triggerRect.left + (triggerRect.width / 2) - (width / 2);

  if (left + width > rightBound) { left = rightBound - width; }
  if (left < leftBound) { left = leftBound; }

  return { left: left - triggerRect.left, viewportLeft: left, width };
};

const Tooltip: React.FC<TooltipProps> = ({
  averageItemHeight,
  closeOnTriggerClick = false,
  constrainToPane = false,
  fixedPosition = false,
  handleClick,
  hugContent = false,
  isTabbable = true,
  mode,
  skipPositioning = false,
  tooltipMaxWidth,
  tooltipText,
  tooltipTriggerId,
  tooltipTriggerStyle,
  tooltipTriggerText,
  tooltipTriggerTextClass,
  tooltipTriggerTextStyle,
}) => {
  const [tooltipVisible, setTooltipVisible] = React.useState(false);

  const suppressNextClickToggle = React.useRef(false);
  const windowBlurredWhileFocused = React.useRef(false);
  const tooltipContentRef = React.useRef<HTMLSpanElement>(null);
  const tooltipId = React.useRef(`tooltip-${uuid()}`).current;
  const tooltipContentId = React.useRef(`tooltip-content-${uuid()}`).current;
  const tooltipRef = React.useRef<HTMLSpanElement>(null);
  const tooltipTriggerRef = React.useRef<HTMLSpanElement>(null);
  const tooltipWrapperRef = React.useRef<HTMLSpanElement>(null);

  const clearPositionStyles = React.useCallback(() => {
    if (tooltipWrapperRef.current) {
      tooltipWrapperRef.current.classList.remove('top-justify');
      tooltipWrapperRef.current.style.bottom = '';
      tooltipWrapperRef.current.style.left = '';
      tooltipWrapperRef.current.style.maxWidth = '';
      tooltipWrapperRef.current.style.position = '';
      tooltipWrapperRef.current.style.top = '';
      tooltipWrapperRef.current.style.width = '';
    }

    if (tooltipRef.current) {
      tooltipRef.current.style.left = '';
      tooltipRef.current.style.maxWidth = '';
      tooltipRef.current.style.transform = '';
      tooltipRef.current.style.width = '';
    }
  }, []);

  const applyDefaultPositionStyles = React.useCallback(() => {
    if (!tooltipRef.current || !tooltipWrapperRef.current) return;

    const targetWidth = tooltipMaxWidth ?? DEFAULT_WIDTH;

    tooltipWrapperRef.current.classList.remove('top-justify');

    // Default path = "skip positioning" -- skip the vertical flip logic but still clamp
    // horizontally so the tooltip can't overflow its pane or the viewport edge. Without
    // this, a trigger near an edge causes the wrapper to extend past it (since it's
    // centered on the trigger via the translateX(-50%) below), which grows
    // body.scrollWidth and adds a horizontal scrollbar.
    if (tooltipTriggerRef.current) {
      const { left, viewportLeft, width } = horizontalPlacement(tooltipTriggerRef.current, targetWidth, constrainToPane);

      // Fixed mode positions the wrapper in viewport coordinates so the
      // tooltip escapes ancestor overflow containers (e.g. horizontally
      // scrollable tables) instead of widening their scrollable area.
      if (fixedPosition) {
        tooltipWrapperRef.current.style.position = 'fixed';
        tooltipWrapperRef.current.style.left = `${viewportLeft}px`;
        tooltipWrapperRef.current.style.top = `${tooltipTriggerRef.current.getBoundingClientRect().bottom}px`;
      } else {
        tooltipWrapperRef.current.style.left = `${left}px`;
      }
      tooltipWrapperRef.current.style.maxWidth = `${width}px`;
      tooltipWrapperRef.current.style.width = `${width}px`;

      tooltipRef.current.style.left = '0';
      tooltipRef.current.style.maxWidth = `${width}px`;
      tooltipRef.current.style.transform = '';
      tooltipRef.current.style.width = `${width}px`;
      return;
    }

    // Trigger ref isn't available yet -- fall back to the original centered
    // default. Rare on subsequent renders but possible on first paint.
    const fallbackWidth = Math.max(Math.min(targetWidth, window.innerWidth - (VIEWPORT_PADDING * 2)), MIN_WIDTH);

    tooltipWrapperRef.current.style.left = '50%';
    tooltipWrapperRef.current.style.maxWidth = `${fallbackWidth}px`;
    tooltipWrapperRef.current.style.width = `${fallbackWidth}px`;

    tooltipRef.current.style.left = '0';
    tooltipRef.current.style.maxWidth = `${fallbackWidth}px`;
    tooltipRef.current.style.transform = 'translateX(-50%)';
    tooltipRef.current.style.width = `${fallbackWidth}px`;
  }, [constrainToPane, fixedPosition, tooltipMaxWidth]);

  const applyPositionStyles = React.useCallback(() => {
    if (!tooltipRef.current || !tooltipTriggerRef.current || !tooltipWrapperRef.current) return;

    const viewportHeight = window.innerHeight;
    const triggerRect = tooltipTriggerRef.current.getBoundingClientRect();
    const tooltipHeight = tooltipRef.current.getBoundingClientRect().height;

    const { left, viewportLeft, width } = horizontalPlacement(tooltipTriggerRef.current, tooltipMaxWidth ?? DEFAULT_WIDTH, constrainToPane);

    const fitsAbove = triggerRect.top - tooltipHeight - VIEWPORT_PADDING > 0;
    const overflowsBottom = triggerRect.bottom + tooltipHeight + VIEWPORT_PADDING > viewportHeight;
    const needsTopJustify = overflowsBottom && fitsAbove;

    tooltipWrapperRef.current.classList.toggle('top-justify', needsTopJustify);
    if (fixedPosition) {
      tooltipWrapperRef.current.style.position = 'fixed';
      tooltipWrapperRef.current.style.left = `${viewportLeft}px`;
      if (needsTopJustify) {
        tooltipWrapperRef.current.style.bottom = `${viewportHeight - triggerRect.top}px`;
        tooltipWrapperRef.current.style.top = 'auto';
      } else {
        tooltipWrapperRef.current.style.bottom = '';
        tooltipWrapperRef.current.style.top = `${triggerRect.bottom}px`;
      }
    } else {
      tooltipWrapperRef.current.style.left = `${left}px`;
    }
    tooltipWrapperRef.current.style.maxWidth = `${width}px`;
    tooltipWrapperRef.current.style.width = `${width}px`;

    tooltipRef.current.style.left = '0';
    tooltipRef.current.style.maxWidth = `${width}px`;
    tooltipRef.current.style.transform = '';
    tooltipRef.current.style.width = `${width}px`;
  }, [constrainToPane, fixedPosition, tooltipMaxWidth]);

  // Shrinks a placed tooltip to its longest line and re-centres it on the trigger. The lines are
  // measured at the full width, and narrowing to the longest of them cannot re-wrap any: every line
  // still fits, and a word that did not fit before still does not.
  const hugToContent = React.useCallback(() => {
    if (!tooltipRef.current || !tooltipContentRef.current || !tooltipTriggerRef.current || !tooltipWrapperRef.current) return;

    const lineWidths = Array.from(tooltipContentRef.current.getClientRects(), rect => rect.width);

    if (!lineWidths.length) return;

    const { paddingLeft, paddingRight } = window.getComputedStyle(tooltipRef.current);
    const contentWidth = Math.ceil(Math.max(...lineWidths)) + (parseFloat(paddingLeft) || 0) + (parseFloat(paddingRight) || 0);
    const { left, viewportLeft, width } = horizontalPlacement(tooltipTriggerRef.current, contentWidth, constrainToPane, Math.min(contentWidth, MIN_WIDTH));

    tooltipWrapperRef.current.style.left = `${fixedPosition ? viewportLeft : left}px`;
    tooltipWrapperRef.current.style.maxWidth = `${width}px`;
    tooltipWrapperRef.current.style.width = `${width}px`;

    tooltipRef.current.style.maxWidth = `${width}px`;
    tooltipRef.current.style.width = `${width}px`;
  }, [constrainToPane, fixedPosition]);

  const showTooltip = React.useCallback(() => {
    if (tooltipVisible) return;

    const activeTooltips = document.getElementsByClassName('visible quill-tooltip');
    Array.from(activeTooltips).forEach(tooltip => tooltip.classList.remove('visible'));

    let textToShow: string = '';

    if (Array.isArray(tooltipText)) {
      if (averageItemHeight && tooltipRef.current) {
        const availableSpace = window.innerHeight - tooltipRef.current.getBoundingClientRect().top;

        if (tooltipText.length * averageItemHeight >= availableSpace) {
          const itemsToFit = Math.floor(availableSpace / averageItemHeight) - 1;
          const remainingItems = tooltipText.length - itemsToFit;

          textToShow = `${tooltipText.slice(0, itemsToFit).map(t => `<span>${t}</span>`).join('<br/><br/>')}<br/><br/><b>...and ${remainingItems} more</b>`;
        } else {
          textToShow = tooltipText.join('<br/>');
        }
      } else {
        textToShow = tooltipText.join('<br/>');
      }
    } else {
      textToShow = tooltipText;
    }

    if (tooltipContentRef.current) {
      tooltipContentRef.current.innerHTML = textToShow;
    }

    setTooltipVisible(true);
  }, [averageItemHeight, tooltipText, tooltipVisible]);

  // Wipes the content and inline positioning. showTooltip rewrites the content on every
  // show and the positioning layout effect rewrites every inline style cleared here, but
  // the wipe still matters while hidden: the trigger always carries aria-describedby (so
  // stale text stays exposed to screen readers), and showTooltip's array-text path
  // measures the tooltip BEFORE the positioning effect runs, so stale styles would skew
  // that measurement.
  const wipeTooltip = React.useCallback(() => {
    if (tooltipContentRef.current) {
      tooltipContentRef.current.innerHTML = '';
    }

    clearPositionStyles();
  }, [clearPositionStyles]);

  const hideTooltip = React.useCallback(() => {
    suppressNextClickToggle.current = false;
    setTooltipVisible(false);

    // Wiping synchronously here while the tooltip still carries its `visible` class is
    // what caused the close artifact: the class only comes off at React's next commit,
    // so the emptied, width-collapsed tooltip painted as a bare 32px black circle (16px
    // padding + 16px corner radius) until the commit landed - longest on busy pages.
    // When the tooltip is NOT class-visible, nothing is painted, so wiping immediately
    // is artifact-safe - and it closes two gaps the commit-keyed effect below cannot
    // cover: a show+hide landing in one React batch (net no state change means no
    // commit, e.g. a mobile tap that fires both the tap toggle and the document
    // click-outside), and re-scrubbing a desynced hidden tooltip on Escape or
    // outside-click.
    if (tooltipRef.current && !tooltipRef.current.classList.contains('visible')) {
      wipeTooltip();
    }
  }, [wipeTooltip]);

  // The normal close path: wipe AFTER the hide commits, once the `visible` class is gone,
  // so the artifact above cannot paint. useLayoutEffect rather than useEffect on purpose:
  // a deferred passive wipe could flush after a rapid re-hover's showTooltip has already
  // written fresh content synchronously, erasing it.
  React.useLayoutEffect(() => {
    if (tooltipVisible) return;

    wipeTooltip();
  }, [wipeTooltip, tooltipVisible]);

  React.useLayoutEffect(() => {
    if (!tooltipVisible) return;

    if (skipPositioning) {
      applyDefaultPositionStyles();
    } else {
      try {
        applyPositionStyles();
      } catch {
        applyDefaultPositionStyles();
      }
    }

    if (hugContent) {
      hugToContent();
    }
  }, [applyDefaultPositionStyles, applyPositionStyles, hugContent, hugToContent, skipPositioning, tooltipVisible]);

  React.useEffect(() => {
    if (!tooltipVisible || skipPositioning) return;

    const handleResize = () => {
      applyPositionStyles();

      if (hugContent) {
        hugToContent();
      }
    };

    window.addEventListener('resize', handleResize);
    // Scroll events don't bubble, so capture is the only way to hear an
    // ancestor scroll container move the trigger out from under a
    // viewport-anchored tooltip.
    if (fixedPosition) {
      window.addEventListener('scroll', handleResize, true);
    }
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [applyPositionStyles, fixedPosition, hugContent, hugToContent, skipPositioning, tooltipVisible]);

  React.useEffect(() => {
    // Containment (rather than target identity) so a click landing on content nested
    // inside the trigger (like an icon img) is not mistaken for an outside click --
    // that treated the same mobile tap that opened the tooltip as a close. The tooltip
    // lives inside the trigger span, so check it first: clicks within the open tooltip
    // keep it open but still reach handleClick, which consumers use to intercept links
    // in the tooltip content.
    const handlePageClick = (e: MouseEvent) => {
      const target = e.target as Node;

      if (!tooltipRef.current || !tooltipTriggerRef.current) return;

      if (tooltipRef.current.contains(target)) {
        handleClick?.(e);
        return;
      }

      if (tooltipTriggerRef.current.contains(target)) return;

      handleClick?.(e);
      hideTooltip();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === ESCAPE) {
        hideTooltip();
      }
    };

    document.addEventListener('click', handlePageClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('click', handlePageClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleClick, hideTooltip]);

  // Mobile browsers emulate a tap as mouseenter -> focus -> click on the same target.
  // When one of the first two events is what opened the tooltip, the same tap's click
  // must not toggle it back closed -- that made every tooltip need a double tap to
  // open (and re-taps after the emulated hover settled would close-then-open). The
  // hover/focus handler flags the show; the click consumes the flag instead of
  // toggling. hideTooltip clears it so a stale flag can't survive a close.
  const handleTriggerHoverOrFocus = React.useCallback(() => {
    if (!tooltipVisible) { suppressNextClickToggle.current = true; }

    showTooltip();
  }, [showTooltip, tooltipVisible]);

  // The popup is a DOM descendant of the trigger span, so clicks on tooltip content
  // bubble through this handler too -- those must never toggle, or tapping a link
  // inside an open tooltip closes it. The document handler above still receives them
  // and forwards to handleClick.
  const handleTooltipClick = React.useCallback((e: React.MouseEvent) => {
    // For triggers whose click changes what the tooltip describes, like a toggle, on every device.
    if (closeOnTriggerClick && !tooltipRef.current?.contains(e.target as Node)) {
      hideTooltip();
      return;
    }

    if (!onMobile()) return;

    if (tooltipRef.current?.contains(e.target as Node)) return;

    if (suppressNextClickToggle.current) {
      suppressNextClickToggle.current = false;
      return;
    }

    if (tooltipVisible) {
      hideTooltip();
    } else {
      showTooltip();
    }
  }, [closeOnTriggerClick, hideTooltip, showTooltip, tooltipVisible]);

  // onBlur is React's focusout, which bubbles: a real click on interactive tooltip
  // content moves focus onto it and fires blur here before the click lands, closing
  // the tooltip out from under the element the user clicked. Only hide when focus
  // actually leaves the trigger subtree (the popup is a descendant, so focus moves
  // into it are contained).
  const handleTriggerBlur = React.useCallback((e: React.FocusEvent) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;

    // A trigger that is still the active element lost focus with the window, not to the page.
    windowBlurredWhileFocused.current = e.currentTarget.contains(document.activeElement);
    hideTooltip();
  }, [hideTooltip]);

  // Coming back to the window hands focus straight back to that trigger. Nobody reached for it, so
  // it should not open the tooltip with nothing hovered.
  const handleTriggerFocus = React.useCallback((e: React.FocusEvent) => {
    const returningToWindow = windowBlurredWhileFocused.current && !e.relatedTarget;
    windowBlurredWhileFocused.current = false;

    if (returningToWindow) return;

    handleTriggerHoverOrFocus();
  }, [handleTriggerHoverOrFocus]);

  const focusClass = mode === 'dark' ? 'focus-on-dark' : 'focus-on-light';
  const tabIndex = isTabbable ? 0 : undefined;
  const triggerClass = `quill-tooltip-trigger ${focusClass} ${tooltipVisible ? 'active' : ''}`;

  const hideForScreenreaders = tooltipText === tooltipTriggerText;

  // aria-describedby only works from the element that actually receives focus: the
  // outer span when the trigger is tabbable, or a focusable element passed as the
  // trigger content (e.g. a labeled button) when it is not. Only intrinsically
  // focusable elements get the injection -- a global ARIA attribute on a
  // non-focusable element does nothing, and on a decorative img (alt="") it
  // creates an axe presentation-role-conflict violation.
  const triggerElement = React.isValidElement(tooltipTriggerText) ? tooltipTriggerText as React.ReactElement : null;

  const triggerContent = !isTabbable && triggerElement && FOCUSABLE_TRIGGER_TAGS.includes(triggerElement.type as string)
    ? React.cloneElement(triggerElement, {
      'aria-describedby': [triggerElement.props['aria-describedby'], tooltipId].filter(Boolean).join(' ')
    })
    : tooltipTriggerText;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <span
      aria-describedby={isTabbable ? tooltipId : undefined}
      className={triggerClass}
      onBlur={handleTriggerBlur}
      onClick={handleTooltipClick}
      onFocus={handleTriggerFocus}
      onMouseEnter={handleTriggerHoverOrFocus}
      onMouseLeave={hideTooltip}
      ref={tooltipTriggerRef}
      style={tooltipTriggerStyle}
      tabIndex={tabIndex}
    >
      <span
        className={tooltipTriggerTextClass}
        id={tooltipTriggerId}
        style={tooltipTriggerTextStyle}
      >
        {triggerContent}
      </span>
      <span
        className="quill-tooltip-wrapper"
        ref={tooltipWrapperRef}
      >
        <span
          aria-hidden={hideForScreenreaders}
          aria-labelledby={tooltipVisible ? tooltipContentId : null}
          className={tooltipVisible ? 'quill-tooltip visible' : 'quill-tooltip'}
          id={tooltipId}
          ref={tooltipRef}
          role={tooltipVisible ? "tooltip" : null}
        >
          <span
            id={tooltipContentId}
            ref={tooltipContentRef}
          />
        </span>
      </span>
    </span>
  );
};

export { Tooltip, }
